import { useEffect, useState } from 'react';

/**
 * Hook that uses localStorage to track if a specific loading screen has been shown before.
 * Returns a boolean indicating if this is the first time seeing the screen,
 * and functions to mark the screen as seen.
 *
 * @param screenId - A unique identifier for the loading screen
 * @returns An object with isFirstTime and markAsSeen
 */
export const useLoadingScreenShown = (
  screenId: string
): {
  isFirstTime: boolean;
  markAsSeen: () => void;
} => {
  const storageKey = `loading-screen-shown-${screenId}`;
  const [isFirstTime, setIsFirstTime] = useState<boolean>(true);

  // Check localStorage on mount to see if this screen has been shown before
  useEffect(() => {
    try {
      const hasBeenShown = localStorage.getItem(storageKey) === 'true';
      setIsFirstTime(!hasBeenShown);
    } catch (error) {
      // If localStorage is not available, assume it's the first time
      console.error('Error accessing localStorage:', error);
      setIsFirstTime(true);
    }
  }, [storageKey]);

  // Function to mark this screen as seen in localStorage
  const markAsSeen = () => {
    // Always update the state regardless of whether localStorage succeeds
    setIsFirstTime(false);

    try {
      localStorage.setItem(storageKey, 'true');
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  };

  return { isFirstTime, markAsSeen };
};

export default useLoadingScreenShown;
